import { HttpErrorResponse } from '@angular/common/http';
import { catchError, EMPTY, MonoTypeOperatorFunction, NEVER, pipe, tap } from 'rxjs';

import type { BusinessCodeReplacementTuple, NotificationHelper } from '../notification-helper.service';

/**
 * Custom RxJS operator to handle HTTP errors with our {@link NotificationHelper}.
 * This is especially useful when dealing with business error codes from backend.
 *
 * This operator:
 * - Catches `HttpErrorResponse` errors.
 * - Invokes the provided `NotificationHelper` service to display error notifications.
 * - Completes the observable gracefully without propagating the error.
 * - Rethrows unexpected errors for further upstream handling.
 *
 * @param notify An instance of the {@link NotificationHelper} service used for error notifications.
 * @param augmentBusinessCode Optional list of business code pairs for augmenting error messages.
 *
 * @returns An `OperatorFunction` that catches HTTP errors, processes them, and completes the stream silently.
 */
export function catchServerError<T>(
  notify: NotificationHelper,
  augmentBusinessCode?: BusinessCodeReplacementTuple[],
): MonoTypeOperatorFunction<T> {
  return pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse) {
        notify.autoHandleError({
          augmentBusinessCode: augmentBusinessCode ?? [],
        })(err);
        return EMPTY; // Complete the observable without propagating further
      }
      throw err; // Rethrow for unexpected errors
    }),
  );
}

/**
 * Custom RxJS operator to handle all errors with our {@link NotificationHelper}.
 * This is especially useful when dealing with business error codes from backend.
 *
 * This operator:
 * - Catches all errors.
 * - Invokes the provided `NotificationHelper` service to display error notifications.
 * - Terminates further emissions (neither error nor completion are emitted)
 *
 * @param notificationHelper An instance of the {@link NotificationHelper} service used for error notifications.
 *
 * @returns An `OperatorFunction` that catches all errors without any further emission, processes them,
 */
export function catchAllErrors<T>(notificationHelper: NotificationHelper): MonoTypeOperatorFunction<T> {
  return pipe(
    tap({ error: notificationHelper.autoHandleError() }),
    catchError(() => NEVER),
  );
}
